/**
 * FRONT-2550 — an open item's tags, each removable, plus a muted "Tagged by
 * EDDY · 5m ago" line when Eddy wrote them. Shared by the zen viewer the Files
 * list opens (`inbox-chat-modal.tsx`) and the `/files/:id` / `/files/doc/:name`
 * pages (`routes/files.tsx`), so an upload and a document behave identically.
 *
 * Removing a tag saves immediately (no Save bar: one click is the whole
 * intent) and says so with a toast; the chip hides at once and comes back only
 * if the save fails — see `item-tag-removal.ts`. While a removal's save is IN
 * FLIGHT every × is disabled (two overlapping saves of two different "complete
 * lists" could resurrect the first tag); the flag clears when the save settles,
 * so several tags can be removed one after another. After a removal keyboard
 * focus moves to the next chip's × (else the previous, else the row's label).
 *
 * Renders nothing when the item has no tags and none were removed here. When
 * the last tag is removed the row stays as a muted "No tags" label — the focus
 * target. Trashed items show their tags read-only (a trashed item is frozen).
 * Keyed by the caller on the item's ref, so a removal's hidden-set never leaks
 * onto another item in the same viewer.
 */

import { useEffect, useRef, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { useSetItemTags, type TaggedItemSource } from '~/hooks/mutations/use-set-item-tags';
import { formatRelativeTime } from '~/lib/relative-time';
import { focusTargetAfterRemoval, removeTagWithRestore, visibleTags } from './item-tag-removal';

export function ItemTags({
  source,
  itemRef,
  tags,
  taggedByEddyAt,
  trashed = false,
  className,
}: {
  source: TaggedItemSource;
  itemRef: string;
  tags: readonly string[];
  taggedByEddyAt?: string | null;
  /** A trashed item is frozen: its tags render without the × buttons. */
  trashed?: boolean;
  className?: string;
}) {
  const setTags = useSetItemTags();
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  // The tag whose × should take focus once the removal has rendered; `null`
  // with `focusLabel` true means "the row's label".
  const focusAfter = useRef<{ tag: string | null } | null>(null);
  const shown = visibleTags(tags, removing);

  useEffect(() => {
    const target = focusAfter.current;
    if (!target || busy) return;
    focusAfter.current = null;
    const el =
      target.tag !== null
        ? Array.from(root.current?.querySelectorAll<HTMLElement>('[data-tag-remove]') ?? []).find(
            (b) => b.dataset.tagRemove === target.tag,
          )
        : root.current?.querySelector<HTMLElement>('[data-testid="item-tags-label"]');
    el?.focus();
  }, [removing, busy]);

  if (shown.length === 0 && removing.size === 0) return null;
  const when = formatRelativeTime(taggedByEddyAt);

  function remove(tag: string) {
    if (busy) return;
    focusAfter.current = { tag: focusTargetAfterRemoval(shown, tag) };
    void removeTagWithRestore({
      tag,
      remaining: shown.filter((t) => t !== tag),
      hide: (t) => setRemoving((s) => new Set(s).add(t)),
      restore: (t) =>
        setRemoving((s) => {
          const next = new Set(s);
          next.delete(t);
          return next;
        }),
      setBusy,
      save: (next) => setTags.mutateAsync({ source, itemRef, tags: next }),
      onSuccess: () => toast.success('Tag removed'),
      onError: (err) => toast.error(`Could not remove tag: ${(err as Error).message}`),
    });
  }

  return (
    <div ref={root} className={className} data-testid="item-tags">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="mr-0.5 text-xs text-muted-foreground outline-none"
          tabIndex={-1}
          data-testid="item-tags-label"
        >
          {shown.length === 0 ? 'No tags' : 'Tags'}
        </span>
        {shown.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className={trashed ? 'py-0 font-normal' : 'gap-0.5 py-0 pr-0.5 pl-2 font-normal'}
            data-testid="item-tag"
          >
            <span className="max-w-[16rem] truncate">{tag}</span>
            {trashed ? null : (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="size-5 rounded-full aria-disabled:cursor-default aria-disabled:opacity-50"
                aria-label={`Remove tag ${tag}`}
                tooltip="Remove tag"
                data-tag-remove={tag}
                aria-disabled={busy ? 'true' : undefined}
                onClick={() => remove(tag)}
              >
                <X weight="bold" aria-hidden />
              </Button>
            )}
          </Badge>
        ))}
      </div>
      {taggedByEddyAt && when && (
        <p className="mt-1 text-[length:var(--text-2xs)] text-muted-foreground/80" data-testid="item-tagged-by-eddy">
          Tagged by EDDY · {when}
        </p>
      )}
    </div>
  );
}
